'use client'

import React, { ChangeEvent, FC, HTMLAttributes, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { DropdownOption } from '@/root/shared-types'
import { useCustomRouter } from '@/root/shared-hooks'
import { ChevronDownIcon } from '@/root/ui/shared/icons/ChevronDownIcon'
import { GroupBadge } from '@/root/ui/tuuleliinid'
import cn from 'classnames'
import { noop } from 'lodash'

interface RouteDropdownProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean
  handleToggleDropdown: () => void
  options: DropdownOption[]
  handleOptionSelection: (id: DropdownOption) => void
  dropdownRef: RefObject<HTMLDivElement | null>
  placeHolder: string
  testId?: string
  selectedOption?: DropdownOption
  selectedOptions?: DropdownOption[]
  disableFilter?: boolean
  displaySelectedOptions?: boolean
  variant?: 'default' | 'filterSelected'
  contextClass?: string
  isFilterDropdown?: boolean
}

export const Dropdown: FC<RouteDropdownProps> = ({
  isOpen,
  handleToggleDropdown,
  selectedOption,
  options,
  handleOptionSelection,
  dropdownRef,
  placeHolder,
  className,
  testId,
  contextClass,
  selectedOptions = [],
  disableFilter = false,
  displaySelectedOptions = true,
  isFilterDropdown = false,
  variant = 'default',
}) => {
  const { locale } = useCustomRouter()

  const [inputValue, setInputValue] = useState<string>('')
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)

  const optionRefs = useRef<(HTMLParagraphElement | null)[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const lastCommittedRef = useRef<string>('')

  const getLabel = useCallback(
    (option?: DropdownOption) => (option ? option.name[locale] || option.name['en'] || '' : ''),
    [locale]
  )

  const filteredOptions = disableFilter
    ? options
    : options.filter((option) =>
        (option.name[locale] ?? option.name['en'] ?? '').toLowerCase().includes(inputValue.toLowerCase())
      )

  const availableOptions =
    variant === 'filterSelected'
      ? filteredOptions.filter((option) => !selectedOptions.some((selected) => selected.id === option.id))
      : filteredOptions

  const setValue = useCallback((v: string) => {
    setInputValue(v)
  }, [])

  const commitSelection = (option: DropdownOption) => {
    handleOptionSelection(option)

    if (variant === 'filterSelected') {
      setValue('')
      lastCommittedRef.current = ''
    } else {
      const v = getLabel(option)
      setValue(v)
      lastCommittedRef.current = v
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isOpen) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!availableOptions.length) return
      setHighlightedIndex((prev) => (prev < availableOptions.length - 1 ? prev + 1 : 0))
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!availableOptions.length) return
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : availableOptions.length - 1))
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < availableOptions.length) {
        commitSelection(availableOptions[highlightedIndex])
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      handleToggleDropdown()
    }
  }

  const handleInputClick = () => {
    if (disableFilter) {
      if (!isOpen) handleToggleDropdown()
      return
    }

    if (!isOpen) {
      const isShowingCommitted = inputValue.length > 0 && inputValue === lastCommittedRef.current

      if (isShowingCommitted) {
        setValue('')
      }

      handleToggleDropdown()
    }
  }

  const prevIsOpenRef = useRef(false)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    prevIsOpenRef.current = isOpen

    if (isOpen && !wasOpen && textareaRef.current) {
      const el = textareaRef.current
      el.focus()
      const end = el.value.length
      el.setSelectionRange(end, end)
    }
  }, [isOpen])

  useEffect(() => {
    if (selectedOption) {
      const committed = getLabel(selectedOption)
      lastCommittedRef.current = committed

      if (!isOpen) {
        setValue(committed)
      }
    }

    if (!isOpen) {
      if (highlightedIndex !== -1) setHighlightedIndex(-1)
      return
    }

    if (highlightedIndex >= availableOptions.length) {
      setHighlightedIndex(-1)
      return
    }

    if (highlightedIndex >= 0) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [isOpen, highlightedIndex, availableOptions.length, selectedOption, getLabel, setValue])

  return (
    <div
      className={cn('relative w-full', {
        'h-8 ps-2': contextClass === 'route-search-dropdown' && isFilterDropdown,
        'border-none': contextClass === 'route-search-dropdown' && !isFilterDropdown,
        'border-neutral-normal-lighter rounded border': contextClass !== 'route-search-dropdown',
      })}
      ref={dropdownRef}
    >
      {!selectedOptions.length ? (
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeHolder}
          onClick={handleInputClick}
          className={cn(
            'text-primary-base flex w-full cursor-text resize-none items-center outline-none',
            {
              'placeholder:text-neutral-medium-light ps-5': contextClass === 'route-search-dropdown',
              'placeholder:text-neutral-darkest overflow-hidden p-3': contextClass !== 'route-search-dropdown',
              'max-h-8 min-h-8 truncate overflow-hidden py-2 !ps-0 text-sm font-normal':
                contextClass === 'route-search-dropdown' && isFilterDropdown,
            },
            className
          )}
          rows={1}
          readOnly={!isOpen || disableFilter}
          onKeyDown={disableFilter ? noop : handleKeyDown}
          data-testid="selected-options"
        />
      ) : (
        <span
          className={cn('border-neutral-normal-lighter flex w-full rounded', {
            'min-h-10 p-3': !isFilterDropdown,
          })}
          onClick={handleToggleDropdown}
          data-testid="selected-options"
        >
          <div className="grid w-full grid-cols-[95%_5%]">
            <span className="flex flex-wrap items-center gap-3">
              {!displaySelectedOptions && <p>{placeHolder}</p>}
              {displaySelectedOptions &&
                selectedOptions.map((option) => <GroupBadge title={option.name['en']} key={option.id} />)}
            </span>
            <ChevronDownIcon />
          </div>
        </span>
      )}

      {isOpen && availableOptions.length > 0 && (
        <div
          className={cn(
            'border-neutral-normal-lighter absolute top-full left-0 z-50 w-full overflow-y-auto rounded border-t bg-white shadow-md',
            {
              'max-h-96 border-t-0': contextClass === 'route-search-dropdown',
              'mt-3': contextClass && isFilterDropdown,
            }
          )}
          data-testid={testId}
        >
          {availableOptions.map((option, index) => (
            <p
              key={option.id}
              ref={(el) => {
                optionRefs.current[index] = el
              }}
              className={cn('min-h-10 w-full cursor-pointer py-2 ps-5 transition-colors duration-300', {
                'bg-base-lightest': highlightedIndex === index,
                'hover:bg-base-lightest': highlightedIndex !== index,
                'text-base-darkest': !isFilterDropdown,
                'text-primary-base flex items-center font-normal': isFilterDropdown,
              })}
              onClick={() => commitSelection(option)}
            >
              {option.name[locale] || option.name['en']}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
